import {
    Component,
    OnInit,
    OnDestroy,
    HostListener,
    ViewChild,
    ElementRef,
    ChangeDetectorRef,
    ChangeDetectionStrategy,
    ViewEncapsulation
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, takeUntil } from 'rxjs';
import { SpotlightService } from './spotlight.service';
import { AISearchResponse } from './spotlight.types';
import { SpotlightResultProcessor, ResultDisplayType, TableHeader } from './spotlight.utils';
import { AuthService } from 'app/core/auth/auth.service';

// Import new components
import {
    SpotlightResultCounterComponent,
    SpotlightResultMonetaryComponent,
    SpotlightResultMultiColumnComponent,
    SpotlightResultMemberProfileComponent,
    SpotlightResultListComponent,
    isMemberProfileData
} from './components';

type SpotlightState = 'initial' | 'searching' | 'response' | 'error';

@Component({
    selector: 'app-spotlight',
    templateUrl: './spotlight.component.html',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatIconModule,
        MatProgressSpinnerModule,
        // Result components
        SpotlightResultCounterComponent,
        SpotlightResultMonetaryComponent,
        SpotlightResultMultiColumnComponent,
        SpotlightResultMemberProfileComponent,
        SpotlightResultListComponent
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpotlightComponent implements OnInit, OnDestroy {
    @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

    isOpen: boolean = false;
    searchQuery: string = '';
    lastQuery: string = '';
    state: SpotlightState = 'initial';
    selectedIndex: number = -1;
    errorMessage: string = '';
    isRateLimitError: boolean = false;

    // Data from API
    suggestions: AISearchResponse[] = [];
    recentSearches: AISearchResponse[] = [];
    currentResponse: AISearchResponse | null = null;

    // Result display properties
    resultDisplayType: ResultDisplayType = 'counter';
    isMemberProfile: boolean = false;
    tableHeaders: TableHeader[] = [];
    tableData: any[] = [];
    allTableData: any[] = [];
    hasMoreData: boolean = false;
    totalRecords: number = 0;
    readonly PAGE_SIZE = 10;
    currentPage: number = 0;
    totalPages: number = 0;

    // Loading states
    loadingSuggestions: boolean = false;
    loadingRecent: boolean = false;

    // User data
    user: any = null;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _spotlightService: SpotlightService,
        private _authService: AuthService
    ) {}

    ngOnInit(): void {
        // Load user data
        const userData = this._authService.getLSUser;
        if (userData) {
            this.user = JSON.parse(userData);
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * Listen for keyboard shortcuts
     */
    @HostListener('document:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent): void {
        // Ctrl+K (Windows/Linux) or Cmd+K (Mac) to open
        if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
            event.preventDefault();
            this.toggle();
            return;
        }

        // ESC to close
        if (event.key === 'Escape' && this.isOpen) {
            event.preventDefault();
            this.close();
            return;
        }

        // Navigation with arrow keys
        if (this.isOpen && this.state === 'initial') {
            const totalItems = this.recentSearches.length + this.suggestions.length;

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.selectedIndex = (this.selectedIndex + 1) % totalItems;
                this._changeDetectorRef.markForCheck();
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.selectedIndex = this.selectedIndex <= 0 ? totalItems - 1 : this.selectedIndex - 1;
                this._changeDetectorRef.markForCheck();
            }

            if (event.key === 'Enter' && this.selectedIndex >= 0) {
                event.preventDefault();
                this.selectItemByIndex(this.selectedIndex);
            }
        }

        // Enter to search when typing
        if (event.key === 'Enter' && this.isOpen && this.searchQuery.trim() && this.selectedIndex === -1) {
            event.preventDefault();
            this.performSearch();
        }
    }

    /**
     * Toggle spotlight visibility
     */
    toggle(): void {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    /**
     * Open spotlight
     */
    open(): void {
        this.isOpen = true;
        this.state = 'initial';
        this.searchQuery = '';
        this.currentResponse = null;
        this.selectedIndex = -1;
        this.errorMessage = '';
        this.isRateLimitError = false;
        this._changeDetectorRef.markForCheck();

        // Load data from API
        this.loadRecentSearches();
        this.loadSuggestions();

        // Focus input after view updates
        setTimeout(() => {
            this.searchInput?.nativeElement?.focus();
        }, 100);
    }

    /**
     * Close spotlight
     */
    close(): void {
        this.isOpen = false;
        this.state = 'initial';
        this.searchQuery = '';
        this.currentResponse = null;
        this.selectedIndex = -1;
        this.errorMessage = '';
        this.isRateLimitError = false;
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Load recent searches from API
     */
    loadRecentSearches(): void {
        this.loadingRecent = true;
        this._spotlightService.getRecentSearches()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    this.recentSearches = response.filter(r => r.success).slice(0, 5);
                    this.loadingRecent = false;
                    this._changeDetectorRef.markForCheck();
                },
                error: () => {
                    this.recentSearches = [];
                    this.loadingRecent = false;
                    this._changeDetectorRef.markForCheck();
                }
            });
    }

    /**
     * Load suggestions from API
     */
    loadSuggestions(): void {
        this.loadingSuggestions = true;
        this._spotlightService.getSuggestions()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    // Filter to get unique questions, excluding recent ones
                    const recentQuestions = new Set(this.recentSearches.map(r => r.question));
                    this.suggestions = response
                        .filter(r => r.success && !recentQuestions.has(r.question))
                        .slice(0, 4);
                    this.loadingSuggestions = false;
                    this._changeDetectorRef.markForCheck();
                },
                error: () => {
                    this.suggestions = [];
                    this.loadingSuggestions = false;
                    this._changeDetectorRef.markForCheck();
                }
            });
    }

    /**
     * Handle overlay click
     */
    onOverlayClick(event: MouseEvent): void {
        if ((event.target as HTMLElement).classList.contains('spotlight-overlay')) {
            this.close();
        }
    }

    /**
     * Select a suggestion or recent search
     */
    selectItem(item: AISearchResponse): void {
        this.lastQuery = item.question;
        this.searchQuery = '';
        this.state = 'searching';
        this.selectedIndex = -1;
        this.errorMessage = '';
        this.isRateLimitError = false;
        this._changeDetectorRef.markForCheck();

        this._spotlightService.search(item.question)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    if (response.success) {
                        this.currentResponse = response;
                        this.processResultData(response.resultData);
                        this.state = 'response';
                    } else {
                        this.errorMessage = response.errorMessage || 'Erro ao processar a consulta.';
                        this.isRateLimitError = response.rateLimitExceeded === true;
                        this.state = 'error';
                    }
                    this._changeDetectorRef.markForCheck();
                },
                error: (error) => {
                    this.errorMessage = error?.error?.errorMessage || 'Erro ao conectar com o servidor.';
                    this.isRateLimitError = error?.error?.rateLimitExceeded === true;
                    this.state = 'error';
                    this._changeDetectorRef.markForCheck();
                }
            });
    }

    /**
     * Select follow-up suggestion
     */
    selectFollowUp(): void {
        if (this.currentResponse?.suggestedFollowup) {
            const followUp = this.currentResponse.suggestedFollowup;
            this.lastQuery = followUp;
            this.searchQuery = '';
            this.currentResponse = null;
            this.state = 'searching';
            this.errorMessage = '';
            this.isRateLimitError = false;
            this._changeDetectorRef.markForCheck();

            this._spotlightService.search(followUp)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe({
                    next: (response) => {
                        if (response.success) {
                            this.currentResponse = response;
                            this.processResultData(response.resultData);
                            this.state = 'response';
                        } else {
                            this.errorMessage = response.errorMessage || 'Erro ao processar a consulta.';
                            this.isRateLimitError = response.rateLimitExceeded === true;
                            this.state = 'error';
                        }
                        this._changeDetectorRef.markForCheck();
                    },
                    error: (error) => {
                        this.errorMessage = error?.error?.errorMessage || 'Erro ao conectar com o servidor.';
                        this.isRateLimitError = error?.error?.rateLimitExceeded === true;
                        this.state = 'error';
                        this._changeDetectorRef.markForCheck();
                    }
                });
        }
    }

    /**
     * Select item by index (for keyboard navigation)
     */
    selectItemByIndex(index: number): void {
        if (index < this.recentSearches.length) {
            this.selectItem(this.recentSearches[index]);
        } else {
            this.selectItem(this.suggestions[index - this.recentSearches.length]);
        }
    }

    /**
     * Check if item is selected
     */
    isSelected(listType: 'recent' | 'suggestion', index: number): boolean {
        if (listType === 'recent') {
            return this.selectedIndex === index;
        } else {
            return this.selectedIndex === this.recentSearches.length + index;
        }
    }

    /**
     * Perform search via API
     */
    performSearch(): void {
        const query = this.searchQuery.trim();
        if (!query || query.length < 5) {
            this.errorMessage = 'A pergunta deve ter pelo menos 5 caracteres.';
            this.state = 'error';
            this._changeDetectorRef.markForCheck();
            return;
        }

        this.lastQuery = query;
        this.searchQuery = '';
        this._executeSearch(query);
    }

    /**
     * Retry the last search
     */
    retrySearch(): void {
        if (this.lastQuery) {
            this._executeSearch(this.lastQuery);
        }
    }

    /**
     * Execute search API call
     */
    private _executeSearch(query: string): void {
        this.state = 'searching';
        this.selectedIndex = -1;
        this.errorMessage = '';
        this.isRateLimitError = false;
        this._changeDetectorRef.markForCheck();

        this._spotlightService.search(query)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    if (response.success) {
                        this.currentResponse = response;
                        this.processResultData(response.resultData);
                        this.state = 'response';
                    } else {
                        this.errorMessage = response.errorMessage || 'Erro ao processar a consulta.';
                        this.isRateLimitError = response.rateLimitExceeded === true;
                        this.state = 'error';
                    }
                    this._changeDetectorRef.markForCheck();
                },
                error: (error) => {
                    this.errorMessage = error?.error?.errorMessage || 'Erro ao conectar com o servidor.';
                    this.isRateLimitError = error?.error?.rateLimitExceeded === true;
                    this.state = 'error';
                    this._changeDetectorRef.markForCheck();
                }
            });
    }

    /**
     * Process result data to determine display type and prepare table data
     */
    processResultData(resultData: any[] | null): void {
        this.resultDisplayType = SpotlightResultProcessor.detectDisplayType(resultData);
        this.tableHeaders = SpotlightResultProcessor.extractHeaders(resultData);
        this.allTableData = resultData || [];
        this.totalRecords = SpotlightResultProcessor.getTotalCount(resultData);
        this.totalPages = Math.ceil(this.totalRecords / this.PAGE_SIZE);
        this.currentPage = 0;
        this.updatePageData();
        this.hasMoreData = this.totalRecords > this.PAGE_SIZE;

        // Detect if it's a member profile (single record with name and/or photo)
        this.isMemberProfile = this.detectMemberProfile(resultData);
    }

    /**
     * Update table data based on current page
     */
    updatePageData(): void {
        const start = this.currentPage * this.PAGE_SIZE;
        const end = start + this.PAGE_SIZE;
        this.tableData = this.allTableData.slice(start, end);
    }

    /**
     * Go to next page
     */
    nextPage(): void {
        if (this.currentPage < this.totalPages - 1) {
            this.currentPage++;
            this.updatePageData();
            this._changeDetectorRef.markForCheck();
        }
    }

    /**
     * Go to previous page
     */
    previousPage(): void {
        if (this.currentPage > 0) {
            this.currentPage--;
            this.updatePageData();
            this._changeDetectorRef.markForCheck();
        }
    }

    /**
     * Detect if result is a member profile (single record with name/photo fields)
     */
    detectMemberProfile(resultData: any[] | null): boolean {
        return isMemberProfileData(resultData);
    }

    /**
     * Get member name from row data
     */
    getMemberName(row: any): string {
        return row?.nome || row?.name || row?.full_name || '-';
    }

    /**
     * Get member photo from row data
     */
    getMemberPhoto(row: any): string | null {
        return row?.foto || row?.avatar || row?.photo || row?.imagem || null;
    }

    /**
     * Get complementary data (exclude name, photo, and hidden fields)
     */
    getComplementaryHeaders(): TableHeader[] {
        const excludeFields = ['nome', 'name', 'full_name', 'foto', 'avatar', 'photo', 'imagem', 'genero', 'gender', 'ativo', 'activated', 'active'];
        return this.tableHeaders.filter(h => !excludeFields.includes(h.key.toLowerCase()));
    }

    /**
     * Format cell value for display
     */
    formatCellValue(value: any, key: string): string {
        return SpotlightResultProcessor.formatValue(value, key);
    }

    /**
     * Check if column is avatar/photo column
     */
    isAvatarColumn(key: string): boolean {
        return SpotlightResultProcessor.isAvatarField(key);
    }

    /**
     * Check if column should be hidden
     */
    shouldHideColumn(key: string): boolean {
        return SpotlightResultProcessor.shouldHideColumn(key);
    }

    /**
     * Get initials from name for avatar fallback
     */
    getInitials(row: any): string {
        const nameKey = Object.keys(row).find(k => k.toLowerCase() === 'nome' || k.toLowerCase() === 'name' || k.toLowerCase() === 'full_name');
        if (nameKey && row[nameKey]) {
            const parts = row[nameKey].split(' ');
            if (parts.length >= 2) {
                return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
            }
            return row[nameKey].charAt(0).toUpperCase();
        }
        return '?';
    }

    /**
     * Get avatar background color based on gender (follows system pattern from membership.component)
     */
    getAvatarBgColor(row: any): string {
        const genderKey = Object.keys(row).find(k => k.toLowerCase() === 'genero' || k.toLowerCase() === 'gender' || k.toLowerCase() === 'sexo');
        if (genderKey) {
            const gender = row[genderKey];
            if (gender === 'male') return 'bg-blue-100 text-gray-900';
            if (gender === 'female') return 'bg-fuchsia-100 text-fuchsia-500';
        }
        return 'bg-blue-100 text-gray-900';
    }

    /**
     * Clear search and go back to initial state
     */
    clearSearch(): void {
        this.searchQuery = '';
        this.lastQuery = '';
        this.state = 'initial';
        this.currentResponse = null;
        this.selectedIndex = -1;
        this.errorMessage = '';
        this.isRateLimitError = false;
        this.resetResultData();
        this.searchInput?.nativeElement?.focus();
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Reset result data properties
     */
    resetResultData(): void {
        this.resultDisplayType = 'counter';
        this.isMemberProfile = false;
        this.tableHeaders = [];
        this.tableData = [];
        this.allTableData = [];
        this.hasMoreData = false;
        this.totalRecords = 0;
        this.currentPage = 0;
        this.totalPages = 0;
    }

    /**
     * Handle input changes
     */
    onInputChange(): void {
        if (this.state === 'response' || this.state === 'error') {
            this.state = 'initial';
            this.currentResponse = null;
            this.errorMessage = '';
        }
        this.selectedIndex = -1;
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Get icon for search item based on question content
     */
    getSearchIcon(question: string): string {
        const q = question.toLowerCase();
        if (q.includes('membro') || q.includes('pessoa') || q.includes('homem') || q.includes('mulher')) {
            return '👥';
        }
        if (q.includes('dízimo') || q.includes('entrada') || q.includes('oferta') || q.includes('valor') || q.includes('total')) {
            return '💰';
        }
        if (q.includes('aniversário') || q.includes('nascimento')) {
            return '🎂';
        }
        if (q.includes('grupo') || q.includes('ministério') || q.includes('líder')) {
            return '⛪';
        }
        if (q.includes('gasto') || q.includes('saída') || q.includes('despesa')) {
            return '📉';
        }
        return '🔍';
    }

    /**
     * Get icon for response based on result
     */
    getResponseIcon(): string {
        if (!this.currentResponse) return '✨';
        const title = this.currentResponse.resultTitle?.toLowerCase() || '';
        const desc = this.currentResponse.resultDescription?.toLowerCase() || '';
        const combined = title + ' ' + desc;

        if (combined.includes('membro') || combined.includes('pessoa') || combined.includes('homem') || combined.includes('mulher')) {
            return '👥';
        }
        if (combined.includes('r$') || combined.includes('reais') || combined.includes('valor')) {
            return '💰';
        }
        return '✨';
    }

    /**
     * Get user's first name
     */
    getUserFirstName(): string {
        const fullName = this.user?.details?.fullName;
        if (!fullName) return 'Usuário';
        return fullName.split(' ')[0];
    }

    /**
     * Get user's initials for avatar
     */
    getUserInitials(): string {
        const fullName = this.user?.details?.fullName;
        if (!fullName) return '?';
        const parts = fullName.split(' ');
        if (parts.length >= 2) {
            return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
        }
        return parts[0].charAt(0).toUpperCase();
    }

    /**
     * Get user's avatar URL
     */
    getUserAvatar(): string | null {
        return this.user?.details?.avatar || null;
    }

    /**
     * Get headers for list display (excluding name, photo, and hidden columns)
     */
    getListDisplayHeaders(): TableHeader[] {
        const excludeFields = ['nome', 'name', 'full_name', 'foto', 'avatar', 'photo', 'imagem'];
        return this.tableHeaders.filter(h =>
            !excludeFields.includes(h.key.toLowerCase()) && !this.shouldHideColumn(h.key)
        );
    }

    /**
     * Check if column is a type/category column (dízimo, oferta, designada, etc.)
     */
    isTypeColumn(key: string): boolean {
        const typeColumns = ['tipo', 'type', 'categoria', 'category', 'tipo_entrada', 'entry_type'];
        return typeColumns.includes(key.toLowerCase());
    }

    /**
     * Check if column is a value/amount column
     */
    isValueColumn(key: string): boolean {
        const valueColumns = ['valor', 'value', 'amount', 'total', 'quantia', 'preco', 'price'];
        return valueColumns.includes(key.toLowerCase());
    }

    /**
     * Check if column is a transaction type column
     */
    isTransactionColumn(key: string): boolean {
        const transactionColumns = ['transacao', 'transação', 'transaction', 'forma_pagamento', 'payment_method', 'metodo'];
        return transactionColumns.includes(key.toLowerCase());
    }

    /**
     * Check if column is a date column
     */
    isDateColumn(key: string): boolean {
        const dateColumns = ['data', 'date', 'data_transacao', 'data_entrada', 'created_at', 'updated_at', 'nascimento', 'birthday'];
        return dateColumns.includes(key.toLowerCase());
    }

    /**
     * Check if column is an age column
     */
    isAgeColumn(key: string): boolean {
        const ageColumns = ['nova_idade', 'idade', 'age', 'new_age'];
        return ageColumns.includes(key.toLowerCase());
    }

    /**
     * Get CSS class for column width based on column type
     */
    getColumnWidthClass(key: string): string {
        if (this.isTypeColumn(key)) return 'w-[100px] text-center';
        if (this.isValueColumn(key)) return 'w-[120px]';
        if (this.isTransactionColumn(key)) return 'w-[100px] text-center';
        if (this.isDateColumn(key)) return 'w-[130px]';
        if (this.isAgeColumn(key)) return 'w-[80px] text-center';
        return 'w-[100px]';
    }

    /**
     * Get CSS class for type badge based on type value
     */
    getTypeBadgeClass(type: string): string {
        if (!type) return 'bg-gray-100 text-gray-600';

        const typeUpper = type.toUpperCase();

        if (typeUpper === 'DÍZIMO' || typeUpper === 'DIZIMO') {
            return 'bg-emerald-100 text-emerald-700';
        }
        if (typeUpper === 'OFERTA') {
            return 'bg-blue-100 text-blue-700';
        }
        if (typeUpper === 'DESIGNADA') {
            return 'bg-purple-100 text-purple-700';
        }
        if (typeUpper === 'SAÍDA' || typeUpper === 'SAIDA') {
            return 'bg-red-100 text-red-700';
        }
        if (typeUpper === 'CAMPANHA') {
            return 'bg-amber-100 text-amber-700';
        }
        if (typeUpper === 'VOTO') {
            return 'bg-indigo-100 text-indigo-700';
        }

        return 'bg-gray-100 text-gray-600';
    }

    /**
     * Format currency value
     */
    formatCurrency(value: any): string {
        if (value === null || value === undefined) return '-';

        const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;

        if (isNaN(numValue)) return String(value);

        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(numValue);
    }

    /**
     * Format date value
     */
    formatDateValue(value: any): string {
        if (!value) return '-';

        // If already formatted as DD/MM/YYYY or similar
        if (typeof value === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
            return value;
        }

        // If in DDMMYYYY format (no separators)
        if (typeof value === 'string' && /^\d{8}$/.test(value)) {
            return `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4, 8)}`;
        }

        // Try to parse as date
        try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('pt-BR');
            }
        } catch {
            // ignore
        }

        return String(value);
    }
}
